import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { paths, type PublicEntitlements } from "@clevis/shared-types";
import { db } from "../lib/firebase";
import { useAuth } from "./useAuth";

// Member-readable entitlements mirror. Fails OPEN when the doc doesn't exist
// yet (pre-billing tenants see everything) — the server still enforces.
export function useEntitlements(): {
  loaded: boolean;
  hasFeature: (key: string) => boolean;
  hasIntegration: (slug: string) => boolean;
  /** Plan limits plus per-company caps (Clevis Ring's ringNumbers / ringMinutes); null or missing means no limit. */
  limits: Record<string, number | null>;
} {
  const { tenantId } = useAuth();
  const [ent, setEnt] = useState<PublicEntitlements | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenantPublicEntitlements(tenantId)), (snap) => {
      setEnt(snap.exists() ? (snap.data() as PublicEntitlements) : null);
      setLoaded(true);
    });
  }, [tenantId]);

  function hasFeature(key: string): boolean {
    if (!ent || Object.keys(ent.features).length === 0) return true; // fail open
    return ent.features[key] !== false;
  }

  // Integrations fail CLOSED: an integration is off until explicitly enabled.
  function hasIntegration(slug: string): boolean {
    return ent?.integrations?.[slug] === true;
  }

  return { loaded, hasFeature, hasIntegration, limits: ent?.limits ?? {} };
}
